import { useMountEffect } from "@react-hookz/web";
import cx from "classnames";
import { readFragment, ResultOf } from "gql.tada";
import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router";
import { usePage } from "../../../hooks/usePage";
import kebabCase from "lodash/kebabCase";
import {
  SolutionsChapterFragment,
  SolutionsSectionById,
} from "../../../queries/entries/SolutionsSectionById";
import { PageModel } from "../../../types/PageModel";
import SolutionsChapter from "./SolutionsChapter";
import SolutionsDesktopMenu from "./SolutionsDesktopMenu";
import SolutionsMobileMenu from "./SolutionsMobileMenu";

const getPageTitle = ({ page, hero }: PageModel) => {
  if (hero) {
    switch (hero?.__typename) {
      case "SolutionsHero":
        return hero.instance.solutionsHero.solutionsHeroTitle;
      default:
        return hero.instance.title;
    }
  }

  return page.title;
};

export const SolutionsSection = ({
  entry: { solutionsSection: entry },
}: {
  entry: ResultOf<typeof SolutionsSectionById>;
}) => {
  const location = useLocation();

  const currentPage = usePage();
  const pageTitle = currentPage ? getPageTitle(currentPage) : "Solutions";

  const sectionRef = useRef<HTMLDivElement>(null);
  const [isExpanded, setIsExpanded] = useState(false);

  const chapters = (entry?.chaptersCollection?.items ?? [])
    .map((item) => readFragment(SolutionsChapterFragment, item))
    .filter(Boolean);

  const chapterRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [activeChapterIndex, setActiveChapterIndex] = useState(0);
  const chapterAmountsInViewRef = useRef<number[]>([]);

  useMountEffect(() => {
    const hash = location.hash;
    if (hash) {
      const chapterEl = document.getElementById(hash.slice(1));
      chapterEl?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  });

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        let mostVisibleIndex = activeChapterIndex;

        entries.forEach((entry) => {
          const visibleArea = entry.intersectionRatio;
          const index = chapterRefs.current.findIndex(
            (el) => el === entry.target
          );

          chapterAmountsInViewRef.current[index] = visibleArea;

          if (
            visibleArea > 0 &&
            visibleArea > chapterAmountsInViewRef.current[activeChapterIndex]
          ) {
            mostVisibleIndex = index;
          }
        });

        if (mostVisibleIndex !== activeChapterIndex) {
          setActiveChapterIndex(mostVisibleIndex);
        }
      },
      { root: null, threshold: Array.from({ length: 11 }, (_, i) => i * 0.1) }
    );

    chapterRefs.current.forEach((ref) => ref && observer.observe(ref));

    return () => {
      observer.disconnect();
    };
  }, [chapters, activeChapterIndex]);

  return (
    <section
      ref={sectionRef}
      className={cx("container-base grid-base py-[120px] transition-colors", {
        "bg-palette-current-deep/20": activeChapterIndex % 2 === 1,
        "bg-palette-current-dark": activeChapterIndex % 2 === 0,
      })}
    >
      <SolutionsDesktopMenu
        chapters={chapters}
        activeChapterIndex={activeChapterIndex}
        setIsExpanded={setIsExpanded}
        pageTitle={pageTitle}
      />
      <div className="dsk:hidden">
        <SolutionsMobileMenu
          chapters={chapters}
          activeChapterIndex={activeChapterIndex}
          setIsExpanded={setIsExpanded}
          isExpanded={isExpanded}
          pageTitle={pageTitle}
          ref={sectionRef}
        />
      </div>
      {chapters.map((chapter, index) => {
        const slug = kebabCase(chapter.menuLabel ?? chapter.eyebrow ?? "");
        return (
          <div
            key={chapter._id}
            id={slug}
            ref={(el) => (chapterRefs.current[index] = el)}
            className="col-span-12 dsk:col-span-17 dsk:col-start-7"
          >
            <SolutionsChapter chapter={chapter} index={index} />
          </div>
        );
      })}
    </section>
  );
};
